import React, { FC, useState } from "react";
import {
  useGetTagListQuery,
  useAddOrUpdateTagMutation,
} from "features/api/contentManagement/contentManagementApiSlice";
import * as Yup from "yup";

import BSButton from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/Stack";

import { PencilSquare } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import RenderList from "./List/RenderList";

import {
  Tag,
  tagsTabFields,
  TagsFormControlsValues,
} from "../contentManagementUtils/tabSchema";
import { Button } from "./Button";
import AddContentForm from "./AddContentForm";
import { TagsTabTypes } from "../contentManagementTypes";

const TagsTab: FC<TagsTabTypes> = () => {
  const { t } = useTranslation();
  const { data: tags } = useGetTagListQuery();
  const [addOrUpdateTag] = useAddOrUpdateTagMutation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState<Tag | null>(null);

  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
  };

  const toggleUpdateForm = (initialValues: Tag | null) => {
    setShowUpdateForm(initialValues);
  };

  const tagsValidationSchema: Yup.ObjectSchema<TagsFormControlsValues> =
    Yup.object().shape({
      name: Yup.string().required(t("חובה להזין שם תגית")),
    });

  const tagsInitialValues: Tag = {
    name: "",
    id: 0,
    disable: false,
    index: (tags?.length ?? 0) + 1,
  };

  const handleCreateOrUpdateTag: (values: Tag) => void = async (
    values: Tag
  ) => {
    const payload = [
      {
        Name: values.name,
        id: values?.id ?? 0,
        disable: values.disable,
        index: values?.index,
      },
    ];

    await addOrUpdateTag(payload).unwrap();

    // close modal
    values?.id ? toggleUpdateForm(null) : toggleCreateForm();
  };

  const handleDisable = async (tag: Tag) => {
    const payload = [
      {
        Name: tag.name,
        id: tag.id,
        Disable: tag.disable,
        index: tag.index,
      },
    ];

    await addOrUpdateTag(payload).unwrap();
  };

  const columns = [
    {
      title: t("מספר תגית"),
      dataIndex: 1,
      key: "id",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 150,
    },
    {
      title: t("שם תגית"),
      dataIndex: 2,
      key: "name",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 150,
    },
    {
      title: t("חסום?"),
      dataIndex: 3,
      key: "actions",
      flex: 1,
      renderCell: (tag: Tag) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="switch"
              id="custom-switch"
              checked={tag.disable}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleDisable({ ...tag, disable: event.target.checked });
              }}
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
    {
      title: "",
      dataIndex: 4,
      key: "actions",
      flex: 1,
      renderCell: (tag: Tag) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <div className={`button-wrapper ${tag.disable ? "disabled" : ""}`}>
              <BSButton key={tag.id} name={tag.name} variant="outline">
                <PencilSquare
                  size={20}
                  color="primary"
                  onClick={() => {
                    toggleUpdateForm(tag);
                  }}
                />
              </BSButton>
            </div>
          </Stack>
        );
      },
      asc: true,
      minWidth: 150,
    },
  ];

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <Button title={t("add")} handleClick={toggleCreateForm} />
      </div>
      <RenderList<Tag>
        data={tags ? [...tags].sort((a, b) => a.index - b.index) : []}
        columns={columns}
        disableField="disable"
        isDraggable
      />
      <AddContentForm
        isOpen={showCreateForm}
        toggle={toggleCreateForm}
        fields={tagsTabFields}
        submitForm={handleCreateOrUpdateTag}
        headerTitle={t("תגית חדשה")}
        approveBtnLabel={t("יצירת תגית")}
        cancelBtnLabel={t("cancel")}
        initialValues={tagsInitialValues}
        validationSchema={tagsValidationSchema}
      />
      <AddContentForm
        isOpen={!!showUpdateForm}
        toggle={toggleUpdateForm}
        fields={tagsTabFields}
        submitForm={handleCreateOrUpdateTag}
        headerTitle={t("עדכן תגית")}
        approveBtnLabel={t("עדכן תגית")}
        cancelBtnLabel={t("cancel")}
        initialValues={showUpdateForm}
        validationSchema={tagsValidationSchema}
      />
    </div>
  );
};

export default TagsTab;
